import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SharedDropzone from '../components/common/SharedDropzone';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { apiService } from '../services/api';
import { logTenderActivity } from '../utils/activityLogger';

interface FormData {
  projectTitle: string;
  clientName: string;
  clientContactPerson: string;
  clientContactEmail: string;
  projectBackground: string;
  projectObjectives: string;
  projectScope: string;
  projectDeliverables: string;
  feeType: string;
  budgetEstimate: string;
  clientPriceSensitivity: string;
  projectDuration: string;
  deliveryType: string;
  evaluationCriteria: string;
  submissionDeadline: string;
  rawTextPasted: string;
}

interface FormSectionProps {
  title: string;
  children: React.ReactNode;
  sx?: object;
}

interface FieldWithHelperProps {
  id: string;
  label: string;
  placeholder?: string;
  helpText?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  multiline?: boolean;
  rows?: number;
  type?: string;
  children?: React.ReactNode;
}

const FormSection: React.FC<FormSectionProps> = ({ title, children, sx }) => {
  const theme = useTheme();
  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 4, ...sx }}>
      <Typography
        variant="h5"
        component="h2"
        sx={{
          mb: 3,
          fontWeight: 'bold',
          borderBottom: `3px solid ${theme.palette.bidlogiq.orange}`,
          pb: 1,
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
};

const FieldWithHelper: React.FC<FieldWithHelperProps> = ({
  id,
  label,
  placeholder,
  helpText,
  value,
  onChange,
  multiline = false,
  rows = 1,
  type = 'text',
  children,
}) => {
  const theme = useTheme();
  if (!children) {
    return (
      <TextField
        fullWidth
        variant="outlined"
        id={id}
        name={id}
        label={label}
        placeholder={placeholder || ''}
        value={value}
        onChange={onChange}
        multiline={multiline}
        rows={rows}
        type={type}
        helperText={helpText}
        InputLabelProps={{
          sx: {
            fontWeight: 'bold',
            color: theme.palette.text.primary,
          },
        }}
        FormHelperTextProps={{
          sx: {
            fontSize: '0.9rem',
            color: theme.palette.text.primary,
          },
        }}
      />
    );
  }
  return (
    <Box>
      {children}
      {helpText && (
        <FormHelperText sx={{ fontSize: '0.9rem', color: 'text.primary' }}>
          {helpText}
        </FormHelperText>
      )}
    </Box>
  );
};

export interface Props {
  isWizardMode?: boolean;
  onSuccess?: (tenderId?: string) => void;
}

const ManualTenderPage: React.FC<Props> = ({ isWizardMode, onSuccess }) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { idToken } = useAuth();

  const isGrant = location.state?.isGrant || false;

  const [formData, setFormData] = useState<FormData>({
    projectTitle: '',
    clientName: '',
    clientContactPerson: '',
    clientContactEmail: '',
    projectBackground: '',
    projectObjectives: '',
    projectScope: '',
    projectDeliverables: '',
    feeType: 'Value-Based Fee',
    budgetEstimate: '',
    clientPriceSensitivity: 'Medium',
    projectDuration: '',
    deliveryType: 'Full-Time',
    evaluationCriteria: '',
    submissionDeadline: '',
    rawTextPasted: '',
  });
  const [supportingFiles, setSupportingFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const isSubmittingRef = useRef<boolean>(false);

  useEffect(() => {
    const fetchDetails = async (): Promise<void> => {
      if (!idToken) {
        return;
      }
      try {
        await apiService.getAccountDetails();
        // Logic to pre-fill company details if needed can be added here
      } catch (_error) {
        showSnackbar('Could not pre-fill company details.', 'warning');
      }
    };
    fetchDetails();
  }, [idToken, showSnackbar]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilesSelected = (files: File[]): void => {
    setSupportingFiles((prev) => [
      ...prev,
      ...files.filter((file) => !prev.some((p) => p.name === file.name)),
    ]);
  };

  const handleRemoveFile = (fileName: string): void => {
    setSupportingFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const handleSubmit = async (): Promise<void> => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    if (!formData.projectTitle || !formData.clientName) {
      showSnackbar('Project Title and Client Name are required.', 'error');
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      return;
    }

    const markdownContent = `
# Request For ${isGrant ? 'Grant' : 'Tender'} (${isGrant ? 'RFG' : 'RFT'}): ${formData.projectTitle}

## 1. Project Details
- **Project Title:** ${formData.projectTitle}
- **${isGrant ? 'Funder' : 'Client / Organisation'} Name:** ${formData.clientName}
- **${isGrant ? 'Funder' : 'Client'} Contact Name:** ${formData.clientContactPerson || 'N/A'}
- **${isGrant ? 'Funder' : 'Client'} Contact Email:** ${formData.clientContactEmail || 'N/A'}


## 2. The 'Why' - Context & Objectives
### Background & Context
${formData.projectBackground || 'No background provided.'}

### Project Objectives
${formData.projectObjectives || 'No objectives provided.'}

## 3. The 'What' - Scope & Deliverables
### Scope of Work
${formData.projectScope || 'No scope provided.'}

### Key Deliverables
${formData.projectDeliverables || `The ${isGrant ? 'grant' : 'tender'} response must include a list of proposed deliverables.`}

## 4. The 'How' - Key Parameters & Constraints
- **Fee Type:** ${formData.feeType}
- **Estimated Budget (e.g., AUD):** ${formData.budgetEstimate || 'N/A'}
- **${isGrant ? 'Funder' : 'Client'} Price Sensitivity:** ${formData.clientPriceSensitivity}
- **Project Duration:** ${formData.projectDuration || 'N/A'}
- **Delivery Type:** ${formData.deliveryType}
- **Deadline / Closing Date:** ${formData.submissionDeadline || 'N/A'}

### Known Evaluation Criteria
${formData.evaluationCriteria || 'No evaluation criteria provided.'}

## 5. Foundational ${isGrant ? 'Grant' : 'Tender'} Documents
### Pasted Raw Text
${formData.rawTextPasted || 'No raw text provided.'}
        `.trim();

    const markdownFile = new File([markdownContent], 'manual-tender-details.txt', {
      type: 'text/plain',
    });
    const allFilesToUpload = [markdownFile, ...supportingFiles];

    try {
      // INTERIM WHITE-LABELING: pass isGrant flag
      const response = await apiService.createTender(allFilesToUpload, 'manual', {
        title: formData.projectTitle,
        tenderIssuer: formData.clientName,
        isGrant,
      });
      showSnackbar(
        `Manual ${isGrant ? 'grant' : 'tender'} submitted successfully! Your new ${isGrant ? 'grant' : 'tender'} is being processed.`,
        'success'
      );
      logTenderActivity.created({
        title: formData.projectTitle,
        type: 'manual',
        source: 'manual_tender_form',
      });
      if (isWizardMode && onSuccess) {
        onSuccess(response.tenderId);
      } else {
        navigate('/dashboard');
      }
    } catch (error: unknown) {
      const err = error as Error & {
        response?: { status?: number; data?: { message?: string } };
      };
      const errorMessage = err.response?.data?.message || err.message || 'Please try again.';
      if (errorMessage.includes('limit') || errorMessage.includes('billing period')) {
        showSnackbar(
          '⚠️ LIMIT REACHED: You have reached your subscription limit for creating tenders. Please upgrade your plan to create more tenders.',
          'warning',
          5000
        );
      } else {
        showSnackbar(`Failed to create manual tender. ${errorMessage}`, 'error');
      }
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const content = (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* INTERIM WHITE-LABELING: Grant Indicator in Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Create a New Manual {isGrant ? 'Grant' : 'Tender'}
        </Typography>
        {isGrant && (
          <Chip
            label="Grant Mode Active"
            color="success"
            variant="outlined"
            sx={{
              fontWeight: 'bold',
              borderWidth: 2,
              backgroundColor: `${theme.palette.success.main}1A`,
            }}
          />
        )}
      </Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
        Imagine you are the {isGrant ? 'funder' : 'client'} issuing this request. Fill out the
        fields below to create a new
        {isGrant ? ' grant' : ' tender'} for your dashboard, where you can then build your response
        assets. All sections are optional, but the more detail you provide, the better the platform
        can assist you with analysis and drafting. Leaving fields blank will generally result in a
        more generic starting point.
      </Typography>
      <FormSection title="1. Project Details">
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid size={{ xs: 12, sm: 12 }}>
            <FieldWithHelper
              id="projectTitle"
              label="Project Title"
              placeholder="e.g., Q3 Marketing Campaign for 'Solstice' Product Launch"
              helpText="The 'headline' of the proposal. Make it concise and descriptive."
              value={formData.projectTitle}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FieldWithHelper
              id="clientName"
              label="Client / Organisation Name"
              placeholder="e.g., Innovate Corp."
              helpText="The name of the organisation this proposal is for."
              value={formData.clientName}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FieldWithHelper
              id="clientContactPerson"
              label="Client Contact Name"
              placeholder="e.g., Jane Doe"
              helpText="The main person you are dealing with on the client's side."
              value={formData.clientContactPerson}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FieldWithHelper
              id="clientContactEmail"
              label="Client Contact Email"
              placeholder="e.g., jane.doe@innovatecorp.com"
              type="email"
              helpText="The primary email for your contact."
              value={formData.clientContactEmail}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="2. The 'Why' - Context & Objectives">
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid size={{ xs: 12 }}>
            <FieldWithHelper
              id="projectBackground"
              label="Background & Context"
              multiline
              rows={6}
              placeholder={
                "e.g., Client's current CRM is outdated, leading to lost sales opportunities..."
              }
              helpText="Describe the situation that led to this project. What problem is the client trying to solve? Why now?"
              value={formData.projectBackground}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FieldWithHelper
              id="projectObjectives"
              label="Project Objectives"
              multiline
              rows={6}
              placeholder={
                'e.g.,\n- Increase lead conversion by 15%\n- Reduce manual data entry for sales team'
              }
              helpText="List the key, high-level goals. What does success look like for the client?"
              value={formData.projectObjectives}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="3. The 'What' - Scope & Deliverables">
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid size={{ xs: 12 }}>
            <FieldWithHelper
              id="projectScope"
              label="Scope of Work"
              multiline
              rows={6}
              placeholder={
                'e.g.,\n1. Discovery and requirements gathering workshops.\n2. Configuration of new CRM platform.'
              }
              helpText="Detail the specific activities and tasks required. State what is in-scope and out-of-scope."
              value={formData.projectScope}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FieldWithHelper
              id="projectDeliverables"
              label="Key Deliverables"
              multiline
              rows={6}
              placeholder={'e.g.,\n- A configured and live CRM system.\n- A final project report.'}
              helpText="List the tangible items that you will hand over to the client."
              value={formData.projectDeliverables}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="4. The 'How' - Key Parameters & Constraints">
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldWithHelper
              id="feeType"
              label="Fee Type"
              helpText="How will you charge for the work?"
              value={formData.feeType}
            >
              <FormControl
                fullWidth
                sx={{ '& .MuiInputLabel-root': { fontWeight: 'bold', color: 'text.primary' } }}
              >
                <InputLabel>Fee Type</InputLabel>
                <Select
                  name="feeType"
                  value={formData.feeType}
                  label="Fee Type"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="Time Accrued">Time Accrued (Hourly/Daily Rate)</MenuItem>
                  <MenuItem value="Value-Based Fee">Value-Based Fee (Fixed Price)</MenuItem>
                  <MenuItem value="Milestone-Based">Milestone-Based</MenuItem>
                </Select>
              </FormControl>
            </FieldWithHelper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldWithHelper
              id="budgetEstimate"
              label="Estimated Budget (e.g., AUD)"
              placeholder="e.g., 75000"
              type="number"
              helpText="Provide your best estimate. This helps the AI tailor the pricing section."
              value={formData.budgetEstimate}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldWithHelper
              id="clientPriceSensitivity"
              label="Client Price Sensitivity"
              helpText="How sensitive is the client to price?"
              value={formData.clientPriceSensitivity}
            >
              <FormControl
                fullWidth
                sx={{ '& .MuiInputLabel-root': { fontWeight: 'bold', color: 'text.primary' } }}
              >
                <InputLabel>Client Price Sensitivity</InputLabel>
                <Select
                  name="clientPriceSensitivity"
                  value={formData.clientPriceSensitivity}
                  label="Client Price Sensitivity"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="Low">Low (Focus on quality/speed)</MenuItem>
                  <MenuItem value="Medium">Medium (Balanced)</MenuItem>
                  <MenuItem value="High">High (Focus on cost)</MenuItem>
                </Select>
              </FormControl>
            </FieldWithHelper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldWithHelper
              id="projectDuration"
              label="Project Duration"
              placeholder="e.g., 8 weeks"
              helpText="The expected total time from start to finish."
              value={formData.projectDuration}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldWithHelper
              id="submissionDeadline"
              label="Deadline / Closing Date"
              placeholder="e.g., 2026/12/25 17:00"
              helpText="The date and time the tender closes (e.g., 2026/12/25 17:00)"
              value={formData.submissionDeadline}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldWithHelper
              id="deliveryType"
              label="Delivery Type"
              helpText="How will your team be engaged?"
              value={formData.deliveryType}
            >
              <FormControl
                fullWidth
                sx={{ '& .MuiInputLabel-root': { fontWeight: 'bold', color: 'text.primary' } }}
              >
                <InputLabel>Delivery Type</InputLabel>
                <Select
                  name="deliveryType"
                  value={formData.deliveryType}
                  label="Delivery Type"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="Full-Time">Full-Time</MenuItem>
                  <MenuItem value="Fractional">Fractional (e.g., 2 days/week)</MenuItem>
                  <MenuItem value="Ad-hoc">Ad-hoc (As needed)</MenuItem>
                  <MenuItem value="Retainer">Retainer (Guaranteed availability)</MenuItem>
                </Select>
              </FormControl>
            </FieldWithHelper>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FieldWithHelper
              id="evaluationCriteria"
              label="Known Evaluation Criteria"
              multiline
              rows={4}
              placeholder={
                'e.g.,\n- Proven experience with similar migrations\n- Ability to meet the aggressive timeline'
              }
              helpText="Crucial! How will they judge your proposal?"
              value={formData.evaluationCriteria}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="5. Foundational Tender Documents">
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid size={{ xs: 12 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Upload Foundational Files (Optional)
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
                Upload RFT files, scope briefs, and direct client correspondence to establish the
                core context for this specific tender. These documents define what the client is
                asking for. Adding files is not required.
              </Typography>
              <Alert severity="error" sx={{ mb: 3, '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Warning: Context Files ONLY
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Do NOT upload past projects, delivery files, case studies, or your company's
                  capability statements here. This section is strictly for documents that define the
                  current tender's requirements.
                </Typography>
              </Alert>
              <SharedDropzone
                onFilesSelected={handleFilesSelected}
                onRemoveFile={handleRemoveFile}
                selectedFiles={supportingFiles}
                type="tender_docs"
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box>
              <FieldWithHelper
                id="rawTextPasted"
                label="Paste Raw Text (from emails, chats)"
                multiline
                rows={10}
                helpText="This is often faster than saving and uploading files."
                value={formData.rawTextPasted}
                onChange={handleChange}
              />
            </Box>
          </Grid>
        </Grid>
      </FormSection>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          size="large"
          sx={{ color: theme.palette.bidlogiq.white, fontWeight: 'bold' }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Create Manual Tender'}
        </Button>
      </Box>
    </Box>
  );

  return isWizardMode ? content : <PageContainer maxWidth={false}>{content}</PageContainer>;
};

export default ManualTenderPage;
